import React from 'react'

import { Box, Button, List, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  CustomCategoriesPageDocument,
  DisableFinanceCustomCategoryDocument,
  EnableFinanceCustomCategoryDocument,
  FinanceCustomCategoryItemFragment,
  FinanceCustomCategoryLegalEntityFragment,
  FinanceCustomSubcategoryItemFragment,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EditCustomSubcategoryModal from 'modules/finance/components/CustomCategories/EditCustomSubcategoryModal'
import SoftDeleteCustomCategoryModal from 'modules/finance/components/CustomCategories/SoftDeleteCustomCategoryModal'

import * as styles from './index.vanilla.css'

interface Props {
  subcategory: FinanceCustomSubcategoryItemFragment
  categories: FinanceCustomCategoryItemFragment[]
  legalEntities: FinanceCustomCategoryLegalEntityFragment[]
  canEditLedgerAccountAssignments: boolean
  isParentEnabled: boolean
}

const SubcategoryItem = ({
  categories,
  legalEntities,
  subcategory,
  canEditLedgerAccountAssignments,
  isParentEnabled,
}: Props) => {
  const [editSubcategoryModalOpen, setEditSubcategoryModalOpen] = React.useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  const { company } = useCurrentSession()

  const [enableCategory] = useMutation(EnableFinanceCustomCategoryDocument, {
    extractorFn: (data) => data.financeMutations.enableFinanceCustomCategory,
  })

  const [disableCategory] = useMutation(DisableFinanceCustomCategoryDocument, {
    extractorFn: (data) => data.financeMutations.disableFinanceCustomCategory,
  })

  const onToggleCategory = async () => {
    let handled

    if (subcategory.enabled) {
      const result = await disableCategory({
        variables: {
          id: subcategory.id,
          companyId: company.id,
        },
        refetchQueries: [CustomCategoriesPageDocument],
      }).then((res) => {
        track(trackingEvents.accounting.settings.categories.disableSubcategory)
        return res
      })

      handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      })
    } else {
      const result = await enableCategory({
        variables: {
          id: subcategory.id,
          companyId: company.id,
        },
        refetchQueries: [CustomCategoriesPageDocument],
      }).then((res) => {
        track(trackingEvents.accounting.settings.categories.enableSubcategory)
        return res
      })

      handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      })
    }

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  return (
    <>
      <List.Item key={subcategory.id} className={styles.listSubitem}>
        <List.Title>
          <Box paddingLeft="s24">
            <Text.Normal color="grey1000">{subcategory.label}</Text.Normal>
          </Box>
        </List.Title>
        <List.ActionItem>
          <Box flexDirection="row" gap="s8">
            <TogglerCheckbox
              onChange={onToggleCategory}
              value={!isParentEnabled ? false : subcategory.enabled}
              disabled={!isParentEnabled}
            />
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.TouchableOption
                  title={i18n.t('finance.settings.accounting.custom_categories.actions.edit')}
                  icon="pencil"
                  onClick={() => {
                    setEditSubcategoryModalOpen(true)
                  }}
                />
                <PopoverMenu.TouchableOption
                  title={i18n.t('finance.settings.accounting.custom_categories.actions.delete')}
                  icon="delete"
                  onClick={() => setDeleteModalOpen(true)}
                  danger
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        </List.ActionItem>
      </List.Item>

      {editSubcategoryModalOpen && (
        <EditCustomSubcategoryModal
          customCategory={subcategory}
          parentCategories={categories}
          legalEntities={legalEntities}
          canEditLedgerAccountAssignments={canEditLedgerAccountAssignments}
          onClose={() => setEditSubcategoryModalOpen(false)}
        />
      )}
      {deleteModalOpen && (
        <SoftDeleteCustomCategoryModal
          category={subcategory}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
    </>
  )
}

export default SubcategoryItem
